import React, { useEffect, useLayoutEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, ChevronRight, Sparkles } from 'lucide-react';

/**
 * iter137 — SchedinaAnalyzerTour
 *
 * Lightweight coach-mark tour (no external dep). Given an ordered list
 * of steps `{targetId, title, body, placement?}`, it renders:
 *   • a full-screen dim overlay
 *   • a punch-out hole around the target element (via SVG mask)
 *   • a tooltip anchored to the target with title/body + Avanti/Fine
 *
 * The component recomputes the target rect on window resize and each
 * step transition; it exposes `data-testid` on every interactive
 * control so testing can drive it precisely.
 *
 * Props:
 *   steps: [{targetId, title, body, placement?: 'top'|'bottom'|'center'}]
 *   onFinish: () => void        // called when user completes or skips
 *   onStepChange?: (i) => void  // fired BEFORE the step index changes
 *                               // (used to switch tab / open modal on demand)
 */
export default function SchedinaAnalyzerTour({ steps, onFinish, onStepChange }) {
  const [idx, setIdx] = useState(0);
  const [rect, setRect] = useState(null);

  const step = steps[idx];

  // Notify the parent BEFORE we try to measure — the parent may need
  // to switch tabs so the target element mounts in the DOM.
  useEffect(() => {
    onStepChange?.(idx);
  }, [idx, onStepChange]);

  // Measure the target rect (with retries — the parent may still be
  // rendering the new tab). Recomputes on window resize + scroll.
  useLayoutEffect(() => {
    if (!step) return;
    let raf;
    let attempts = 0;
    const measure = () => {
      const el = step.targetId ? document.querySelector(`[data-testid="${step.targetId}"]`) : null;
      if (el) {
        const r = el.getBoundingClientRect();
        setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
      } else if (attempts++ < 30) {
        raf = requestAnimationFrame(measure);
      } else {
        // Give up quietly: we'll fall back to a centered tooltip.
        setRect(null);
      }
    };
    measure();
    const onWin = () => measure();
    window.addEventListener('resize', onWin);
    window.addEventListener('scroll', onWin, true);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', onWin);
      window.removeEventListener('scroll', onWin, true);
    };
  }, [step]);

  if (!step) return null;

  const isLast = idx === steps.length - 1;
  const advance = () => (isLast ? onFinish() : setIdx((i) => i + 1));

  // Position the tooltip relative to the target rect. Prefers `bottom`
  // placement (below the target); if the target is in the lower third
  // of the viewport, flip to `top`. Falls back to viewport-centered
  // when the target isn't found (step.placement='center' does the same).
  const vw = typeof window !== 'undefined' ? window.innerWidth : 400;
  const vh = typeof window !== 'undefined' ? window.innerHeight : 800;
  const TT_W = Math.min(340, vw - 24);
  const pad = 12;

  let ttStyle;
  if (rect && step.placement !== 'center') {
    const wantBelow = step.placement === 'bottom' || (!step.placement && rect.top + rect.height < vh * 0.6);
    const top = wantBelow
      ? Math.min(vh - 220, rect.top + rect.height + 14)
      : Math.max(pad, rect.top - 220);
    const left = Math.min(vw - TT_W - pad, Math.max(pad, rect.left + rect.width / 2 - TT_W / 2));
    ttStyle = { top, left, width: TT_W };
  } else {
    ttStyle = {
      top: vh / 2 - 110,
      left: Math.max(pad, vw / 2 - TT_W / 2),
      width: TT_W,
    };
  }

  const holePad = 8;
  const hole = rect
    ? {
        x: Math.max(0, rect.left - holePad),
        y: Math.max(0, rect.top - holePad),
        w: rect.width + holePad * 2,
        h: rect.height + holePad * 2,
        r: 12,
      }
    : null;

  return createPortal(
    <div
      className="fixed inset-0 z-[9999]"
      role="dialog"
      aria-modal="true"
      data-testid="schedina-tour-root"
    >
      {/* Dim overlay with punch-out hole via SVG mask */}
      <svg className="absolute inset-0 w-full h-full pointer-events-auto" style={{ mixBlendMode: 'normal' }}>
        <defs>
          <mask id="tour-mask">
            <rect width="100%" height="100%" fill="white" />
            {hole && (
              <rect x={hole.x} y={hole.y} width={hole.w} height={hole.h} rx={hole.r} ry={hole.r} fill="black" />
            )}
          </mask>
        </defs>
        <rect width="100%" height="100%" fill="rgba(0,0,0,0.72)" mask="url(#tour-mask)" />
        {hole && (
          <rect
            x={hole.x}
            y={hole.y}
            width={hole.w}
            height={hole.h}
            rx={hole.r}
            ry={hole.r}
            fill="none"
            stroke="rgba(6,182,212,0.9)"
            strokeWidth="2"
          />
        )}
      </svg>

      {/* Tooltip card */}
      <div
        className="absolute rounded-2xl border border-cyan-400/30 bg-[#111827]/95 backdrop-blur-xl p-4 shadow-2xl"
        style={ttStyle}
        data-testid="schedina-tour-tooltip"
      >
        <div className="flex items-start justify-between gap-3 mb-2">
          <div className="flex items-center gap-2 min-w-0">
            <Sparkles className="w-4 h-4 text-cyan-400 shrink-0" />
            <h3 className="text-sm font-black uppercase tracking-wider text-cyan-300 truncate">
              {step.title}
            </h3>
          </div>
          <button
            onClick={onFinish}
            className="text-gray-400 hover:text-white shrink-0"
            aria-label="Chiudi tutorial"
            data-testid="schedina-tour-close-btn"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        <p className="text-[12px] text-gray-300 leading-relaxed">{step.body}</p>
        <div className="mt-3 flex items-center justify-between">
          <span className="text-[10px] text-gray-500 font-bold uppercase tracking-wider">
            {idx + 1} / {steps.length}
          </span>
          <button
            onClick={advance}
            className="px-4 py-2 rounded-xl bg-gradient-to-r from-cyan-500 to-purple-500 hover:from-cyan-400 hover:to-purple-400 text-[11px] font-black uppercase tracking-wider text-white flex items-center gap-1.5"
            data-testid="schedina-tour-next-btn"
          >
            {isLast ? 'Ho capito' : 'Avanti'}
            <ChevronRight className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
